import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, X } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: MultiSelectOption[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  /** Show a search input inside the dropdown when there are many options */
  searchThreshold?: number;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  searchThreshold = 10,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const showSearch = options.length > searchThreshold;

  useEffect(() => {
    if (!open) { setSearch(""); return; }
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    // Auto-focus search input when dropdown opens
    if (showSearch) setTimeout(() => searchRef.current?.focus(), 0);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, showSearch]);

  // Group options if any have a group field
  const hasGroups = options.some((o) => o.group);
  const groups: Array<{ name: string | null; items: MultiSelectOption[] }> = [];
  if (hasGroups) {
    const map = new Map<string, MultiSelectOption[]>();
    for (const opt of options) {
      const g = opt.group ?? "";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(opt);
    }
    for (const [name, items] of map) {
      groups.push({ name: name || null, items });
    }
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {selected.size > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
            {selected.size}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] max-h-[320px] rounded-md border border-border bg-background shadow-md flex flex-col">
          {showSearch && (
            <div className="p-1.5 border-b border-border">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-sm border border-input bg-background px-2 py-1 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          )}
          <div className="overflow-y-auto p-1 max-h-[260px]">
          {selected.size > 0 && !search && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent"
              onClick={() => { onClear(); setOpen(false); }}
            >
              <X className="h-3 w-3" />
              Clear all
            </button>
          )}

          {(() => {
            const q = search.toLowerCase().trim();
            const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

            if (q && filtered.length === 0) {
              return <p className="px-2 py-3 text-center text-sm text-muted-foreground">No matches</p>;
            }

            if (hasGroups) {
              const filteredGroups: Array<{ name: string | null; items: MultiSelectOption[] }> = [];
              const fMap = new Map<string, MultiSelectOption[]>();
              for (const opt of filtered) {
                const g = opt.group ?? "";
                if (!fMap.has(g)) fMap.set(g, []);
                fMap.get(g)!.push(opt);
              }
              for (const [name, items] of fMap) {
                filteredGroups.push({ name: name || null, items });
              }
              return filteredGroups.map((g) => (
                <div key={g.name ?? "__ungrouped"}>
                  {g.name && (
                    <div className="px-2 pt-2 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {g.name}
                    </div>
                  )}
                  {g.items.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent ${g.name ? "pl-5" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(opt.value)}
                        onChange={() => onToggle(opt.value)}
                        className="h-3.5 w-3.5 rounded border-border"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ));
            }

            return filtered.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selected.has(opt.value)}
                  onChange={() => onToggle(opt.value)}
                  className="h-3.5 w-3.5 rounded border-border"
                />
                {opt.label}
              </label>
            ));
          })()}
          </div>
        </div>
      )}
    </div>
  );
}
